import {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {API_URL} from "../../settings/local_settings";
import {APIClient} from "../../utils/APIClient";
import {LocalStorage} from "../../utils/LocalStorage";
import {IntercomScript} from "../../IntercomScript";
import {SupportChatService} from "./SupportChatService";
import {SupportChatBubble} from "./SupportChatBubble";

// SupportChatEntry — punto de entrada de la burbuja de chat de soporte (Reemplazo de Intercom,
// Fase 3). Va a reemplazar a <IntercomScript/> en App.js (el swap de App.js lo hace T2-B).
//
// Al montar / cambiar de sesión hace fetch de get-logged-user-data/ (solo si hay user logueado)
// y decide:
//   - support_chat_enabled === true → habilita el service y renderiza <SupportChatBubble/>.
//   - si no                          → renderiza <IntercomScript/> (el componente existente, sin tocar).
// Sin user logueado → <IntercomScript/> (maneja su propio shutdown de Intercom).
//
// El fallback a IntercomScript garantiza un revert de 1 línea en App.js.
//
// La sesión se vuelve a mirar en CADA navegación, no sólo al montar: el logout es SPA (navega a
// /login sin recargar), y sin esto la burbuja quedaba montada con el estado del usuario anterior —
// la tarjeta de un mensaje del equipo seguía en la pantalla de login, y el siguiente usuario de esa
// pestaña podía verla (y marcarla leída). Sin usuario se desmonta, y se monta de nuevo por usuario
// (`key`), así nada del anterior sobrevive.
//
// ⚠️ Lo pendiente del chat se vacía sólo en un LOGOUT (había una sesión), no al llegar sin sesión:
// el link del mail (`?support_conversation=<id>`) se lee al cargar y se saca de la URL, así que si la
// persona llega deslogueada esa conversación vive sólo en `SupportChatService` hasta que entra (el
// login es SPA). Vaciarlo «porque no hay usuario» la perdía (regresión de `ce68b5a`).
export const SupportChatEntry = () => {
    const {pathname} = useLocation();
    // null = todavía no resolvimos, o es un usuario del portal (no se monta nada);
    // true = la burbuja; false = Intercom (también sin user).
    const [chatEnabled, setChatEnabled] = useState(null);
    const [agencyId, setAgencyId] = useState(null);
    const [userId, setUserId] = useState(null);
    // El usuario cuya sesión ya se resolvió (la burbuja montada es la suya).
    const trackedUserIdRef = useRef(null);
    // El último usuario con sesión que vio esta pestaña, aunque su sesión no haya terminado de
    // resolverse: distingue un logout o un cambio de cuenta de «llegué sin sesión».
    const seenUserIdRef = useRef(null);

    const resolveUserSession = useCallback(() => {
        const loggedUserIdStr = LocalStorage.fetchUserId();
        const loggedUserId = loggedUserIdStr ? parseInt(loggedUserIdStr, 10) : null;

        if (!loggedUserId) {
            // Sin user → Intercom (que se ocupa de su propio shutdown). En un LOGOUT, nada pendiente
            // del chat (una conversación a abrir, un prefill) le corresponde al usuario siguiente.
            // Llegar sin sesión no es un logout: el link del mail queda pendiente para cuando entre.
            if (seenUserIdRef.current !== null) SupportChatService.resetSession();
            seenUserIdRef.current = null;
            trackedUserIdRef.current = null;
            setChatEnabled(false);
            setAgencyId(null);
            setUserId(null);
            return;
        }

        // El usuario del portal es cliente de la AGENCIA, no de Dashbo: no ve nuestro chat (los
        // endpoints de soporte le dan 403 y el saludo rompe la marca de la agencia) ni Intercom, que
        // tampoco mira el tipo de usuario. `null` no monta nada (wa#220, iVirtual, 16/10).
        if (LocalStorage.isClientUser()) {
            if (seenUserIdRef.current !== null && seenUserIdRef.current !== loggedUserId) {
                SupportChatService.resetSession();
            }
            seenUserIdRef.current = loggedUserId;
            trackedUserIdRef.current = null;
            SupportChatService.setEnabled(false);
            setChatEnabled(null);
            setAgencyId(null);
            setUserId(null);
            return;
        }

        // Evitar re-fetch para el mismo user ya resuelto.
        if (loggedUserId === trackedUserIdRef.current) {
            return;
        }

        if (seenUserIdRef.current !== null && seenUserIdRef.current !== loggedUserId) {
            // Cambio de cuenta sin logout (se entró con otra en otra pestaña): la burbuja de la
            // anterior se saca YA, no cuando termine de resolverse la nueva.
            SupportChatService.resetSession();
            trackedUserIdRef.current = null;
            setChatEnabled(null);
            setAgencyId(null);
            setUserId(null);
        }
        seenUserIdRef.current = loggedUserId;

        new APIClient(API_URL + 'get-logged-user-data/').call('GET', {}, {cache: true}).then((data) => {
            // Si mientras tanto la sesión cambió (logout, otra cuenta), esta respuesta ya no es de nadie.
            if (seenUserIdRef.current !== loggedUserId) return;
            if (data && data.status === 'OK') {
                trackedUserIdRef.current = loggedUserId;
                const enabled = data.support_chat_enabled === true;
                SupportChatService.setEnabled(enabled);
                setChatEnabled(enabled);
                setAgencyId(enabled ? (data.agency_id ?? null) : null);
                setUserId(loggedUserId);
                if (enabled && typeof window.Intercom === 'function') {
                    // Shutdown defensivo: si Intercom quedó booteado por cualquier
                    // camino (carga previa, HMR, doble render), su launcher vive
                    // FUERA del árbol de React y desmontar <IntercomScript/> no lo
                    // remueve — sin esto conviven los dos globitos.
                    window.Intercom('shutdown');
                }
            }
        });
    }, []);

    // Al montar y en cada navegación (el logout SPA es una navegación a /login).
    useEffect(() => {
        resolveUserSession();
    }, [pathname, resolveUserSession]);

    // Reaccionar a login/logout en otra pestaña (mismo patrón que IntercomScript).
    useEffect(() => {
        window.addEventListener('storage', resolveUserSession);
        return () => {
            window.removeEventListener('storage', resolveUserSession);
        };
    }, [resolveUserSession]);

    if (chatEnabled === null) {
        // Decisión pendiente (fetch en vuelo): NO montar IntercomScript todavía.
        // Si lo montáramos como default, Intercom bootea su launcher fuera del árbol
        // de React y desmontarlo después no lo saca del DOM → quedarían los dos
        // globitos conviviendo. El delay es un blink (el fetch cachea).
        return null;
    }
    if (chatEnabled === true) {
        return <SupportChatBubble key={userId} agencyId={agencyId}/>;
    }
    return <IntercomScript/>;
};
